import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, MenuItem, TextField, Typography } from "@mui/material";

import { IPD_COLORS, ipdElevatedInputSx, IpdButton, IpdSectionCard } from "../../../ipd/utils";
import { getEdServices, saveEdService, updateEdService } from "../../api/emergencyApi";

const money = (fee) => (fee == null ? "Not set" : `₹${Number(fee).toLocaleString("en-IN")}`);

/** One fee, shown as a button that turns into an amount field. */
function FeeEditor({ label, fee, onSave, busy }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const valid = value.trim() !== "" && Number(value) >= 0;

  if (!editing) {
    return (
      // IpdButton does not forward aria-label, so the group names the fee.
      <Box role="group" aria-label={`Fee for ${label}`}>
        <IpdButton
          size="small"
          variant="outlined"
          title={`Set fee for ${label}`}
          onClick={() => {
            setValue(fee == null ? "" : String(Number(fee)));
            setEditing(true);
          }}
        >
          {money(fee)}
        </IpdButton>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        size="small"
        type="number"
        autoFocus
        sx={{ ...ipdElevatedInputSx, width: 120 }}
        InputProps={{ startAdornment: <Box sx={{ mr: 0.5 }}>₹</Box> }}
        inputProps={{ "aria-label": `Fee for ${label}`, min: 0 }}
      />
      <IpdButton
        size="small"
        disabled={!valid || busy}
        onClick={async () => {
          await onSave(value.trim());
          setEditing(false);
        }}
      >
        Save
      </IpdButton>
      <IpdButton size="small" variant="outlined" onClick={() => setEditing(false)}>
        Cancel
      </IpdButton>
    </Box>
  );
}

/**
 * The emergency department's own fees: registration, consultation and a
 * specialist called in, and the hospital's list of procedures and equipment.
 * Each is charged automatically when it happens on a visit. Changing a fee
 * needs the emergency pricing permission; everyone else sees the list read-only.
 */
export default function EdServicesSection() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [newItem, setNewItem] = useState({ kind: "procedure", name: "", fee: "" });

  const load = useCallback(async () => {
    try {
      setData(await getEdServices());
      setError("");
    } catch (e) {
      setError(e?.message || "Could not load the emergency fees");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, message) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      setNotice(message);
      await load();
    } catch (e) {
      setError(e?.message || "That did not work");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return error ? <Alert severity="error">{error}</Alert> : null;
  const canPrice = data.can_price;
  const addReady = newItem.name.trim() && newItem.fee.trim() !== "" && Number(newItem.fee) >= 0;

  return (
    <IpdSectionCard
      title="ED services & fees"
      subtitle="Registration, consultation, procedures and equipment — charged automatically when they happen"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {!canPrice && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only staff with the emergency pricing permission can change these fees.
        </Alert>
      )}

      {data.fixed.map((f) => (
        <Box
          key={f.code}
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}
        >
          <Typography>{f.name}</Typography>
          {canPrice ? (
            <FeeEditor
              label={f.name}
              fee={f.fee}
              busy={busy}
              onSave={(fee) => run(() => saveEdService({ code: f.code, fee }), `${f.name} fee saved.`)}
            />
          ) : (
            <Typography>{money(f.fee)}</Typography>
          )}
        </Box>
      ))}

      <Typography sx={{ mt: 3, mb: 1, fontWeight: 700, fontSize: 14 }}>Procedures & equipment</Typography>
      {data.items.length === 0 && (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textMuted }}>
          Nothing on the list yet.
        </Typography>
      )}
      {data.items.map((s) => (
        <Box
          key={s.service_id}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            opacity: s.active ? 1 : 0.5,
          }}
        >
          <Typography>
            {s.name}{" "}
            <Typography component="span" sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
              ({s.kind})
            </Typography>
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {canPrice ? (
              <FeeEditor
                label={s.name}
                fee={s.fee}
                busy={busy}
                onSave={(fee) => run(() => updateEdService(s.service_id, { fee }), `${s.name} fee saved.`)}
              />
            ) : (
              <Typography>{money(s.fee)}</Typography>
            )}
            {canPrice && (
              <IpdButton
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() =>
                  run(
                    () => updateEdService(s.service_id, { active: !s.active }),
                    s.active ? `${s.name} taken off the list.` : `${s.name} restored.`
                  )
                }
              >
                {s.active ? "Deactivate" : "Restore"}
              </IpdButton>
            )}
          </Box>
        </Box>
      ))}

      {canPrice && (
        <Box sx={{ display: "flex", gap: 1, mt: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            select
            size="small"
            value={newItem.kind}
            sx={{ ...ipdElevatedInputSx, width: 150 }}
            inputProps={{ "aria-label": "New item type" }}
            onChange={(e) => {
              const kind = e.target.value;
              setNewItem((n) => ({ ...n, kind }));
            }}
          >
            <MenuItem value="procedure">Procedure</MenuItem>
            <MenuItem value="equipment">Equipment</MenuItem>
          </TextField>
          <TextField
            size="small"
            placeholder="Name"
            value={newItem.name}
            sx={ipdElevatedInputSx}
            inputProps={{ "aria-label": "New item name" }}
            onChange={(e) => {
              const name = e.target.value;
              setNewItem((n) => ({ ...n, name }));
            }}
          />
          <TextField
            size="small"
            type="number"
            placeholder="Fee"
            value={newItem.fee}
            sx={{ ...ipdElevatedInputSx, width: 120 }}
            inputProps={{ "aria-label": "New item fee", min: 0 }}
            onChange={(e) => {
              const fee = e.target.value;
              setNewItem((n) => ({ ...n, fee }));
            }}
          />
          <IpdButton
            size="small"
            disabled={!addReady || busy}
            onClick={() => {
              const name = newItem.name.trim();
              run(async () => {
                await saveEdService({ kind: newItem.kind, name, fee: newItem.fee.trim() });
                setNewItem((n) => ({ ...n, name: "", fee: "" }));
              }, `${name} added.`);
            }}
          >
            Add
          </IpdButton>
        </Box>
      )}
    </IpdSectionCard>
  );
}
